/** @module csi/table/ScrollDataFixed */
define([
	"dojo/_base/declare",
	"dojo/_base/event",
	"dojo/keys",
	"dojo/on",
	"dojo/touch",
	"dijit/focus",
	"underscore",
	"csi/action",
	"csi/api",
	"csi/constants",
	"csi/heading/_base",
	"csi/html",
	"csi/locale",
	"csi/state",
	"csi/table",
	"csi/types",
	"csi/widget/HorizontalSlider",
	"csi/widget/Stateful"
], function(declare, event, keys, on, touch, focus, _, csiAction, api, constants, csiHeading, html, locale, state, csiTable, types, HorizontalSlider, Stateful) {

	// Missing functionality:
	//	1. html.showWait on lazy loading does not show well if the data is already cached - there is no timeout to show it

	// Possible optimizations:
	// 	1. is there a difference between csi-table-frozen-out setting display none and setting nowrap to true with column width of 0? If not, then no need to update col span?
	//	2. convert footer into regular div if switch all tables to fixed width - we're not managing colspan anyways

	const CSS_COLUMN_RESIZE = "csi-table-column-resize";
	const CSS_COLUMN_RESIZE_CONTAINER = "csi-table-column-resize-container";
	const CSS_COLUMN_RESIZE_HEADER = "csi-table-column-resize-header";
	const CSS_EXTRA_HIDE = "csi-table-hide-extra-column";
	const CSS_EXTRA_SHOW = "csi-table-show-extra-column";
	const CSS_FROZEN = "csi-table-frozen";
	const CSS_FROZEN_TABLE_HEADER_ACTIVE = "csi-table-frozen-active";
	const CSS_FROZEN_MAX = "csi-table-frozen-scroll-max";
	const CSS_FROZEN_MIN = "csi-table-frozen-scroll-min";
	const CSS_FROZEN_SLIDER = "csi-table-frozen-scrollbar";
	const CSS_HEADER_OVERFLOW_HEIGHT = "csi-table-header-overflow-height";
	const CSS_NATURAL_HORIZONTAL_SCROLL = "csi-table-natural-horizontal-scroll";
	const CSS_HORIZONTAL_SCROLL = "csi-table-horizontal-scroll";
	const CSS_PLACEHOLDER = "csi-table-placeholder";
	const CSS_PLACEHOLDER_NO_FILTER = "csi-table-placeholder-no-filter";
	const CSS_PLACEHOLDER_NO_FOOTER = "csi-table-placeholder-no-footer";
	const CSS_POTENTIAl_COLUMN_INDICATOR = "csi-table-potential-column";
	const PROP_FIXEDCOLUMNWIDTHS = "fixedColumnWidths";
	const RESIZE_TIMEOUT = 25;
	const RESIZE_NODE_HALF_WIDTH = 3;
	const SCROLL_TIMEOUT = 50;
	const TRACKPAD_X_RECENT_SCROLL_TIMEOUT = 200;
	const TRACKPAD_X_RESPONSE_RATE_OFFSET = 25;
	const TRACKPAD_X_RESPONSE_RATE_MAX = 90;
	const TRACKPAD_X_RESPONSE_RATE_MIN = 60;
	const TRACKPAD_X_SCROLL_THRESHOLD = 30;
	const TRACKPAD_Y_RESET_X_THRESHOLD = 80;

	let _footHeight = 0;

	// properties used for column resizing
	let _resizeColumn;
	let _resizeColumnCell;
	let _resizeHandles;
	let _resizeLastX;
	let _resizeNewColumnWidth;
	let _resizeOriginalContainerWidth;
	let _resizeOriginalColumnWidth;
	let _resizeReleased;
	let _resizeThrottle;
	let _resizeTimerDelta;
	let _resizeTimerHandle;

	const COLUMN_INDICATOR_KEY_POTENTIAL = "potential";
	const COLUMN_INDICATOR_KEY_FREEZEING = "freezeing";
	const COLUMN_INDICATOR_KEY_FROZEN = "frozen";

	const FROZEN_COLUMN_INDICATOR_MAP = {
		[COLUMN_INDICATOR_KEY_POTENTIAL]: "TablePotentialFrozenColumnBorderColor",
		[COLUMN_INDICATOR_KEY_FREEZEING]: "TableFreezingColumnBorderColor",
		[COLUMN_INDICATOR_KEY_FROZEN]: "TableBorderColor"
	};

	/**
	 * Collect overflow information about header cells
	 * @param {DOMNode} containerNode
	 * @param {Object[]} overflowCells
	 */
	function _collectOverflowCells(containerNode, overflowCells) {
		// scrollHeight is slightly larger even when no overflow because of line height, so ignore partial overflow with tolerance
		const heightTolerance = 8;
		const contents = containerNode.querySelectorAll(`.${csiTable.CSS_HEADER_CONTENT}`);

		for (let domNode of contents) {
			const th = html.getParentByTagName(domNode, "th");
			const overflowsHeight = html.elementOverflows(domNode, heightTolerance);
			const overflows = overflowsHeight || html.elementOverflowsWidth(domNode);
			overflowCells.push({th, overflows, overflowsHeight});
		}
	}

	/**
	 * Apply overflow information to header cells
	 * @param {Object[]} overflowCells
	 */
	function _processOverflowCells(overflowCells) {
		overflowCells.forEach(obj => {
			const {th, overflows, overflowsHeight} = obj;
			th.classList.toggle(CSS_HEADER_OVERFLOW_HEIGHT, overflowsHeight);
			th.classList.toggle(csiTable.CSS_HEADER_OVERFLOW, overflows);
		});
	}

	/**
	 * Return pixel increment per page based on available area to scroll and full amount of pixels to scroll
	 * @param {number} availableScrollWidth
	 * @param {number} fullScrollWidth
	 * @return {number}
	 */
	function _getPageIncrement(availableScrollWidth, fullScrollWidth) {
		// ensure at least 10 pages if full scroll width is not 10 times larger than the available width to scroll
		const n = fullScrollWidth;
		const pages = Math.max(Math.ceil(n / availableScrollWidth), 10);
		return Math.ceil(n / pages);
	}

	/**
	 * Return pixel increment per step (keyboard or arrow button) based on available area to scroll and full amount of pixels to scroll
	 * @param {number} availableScrollWidth
	 * @param {number} fullScrollWidth
	 * @return {number}
	 */
	function _getStepIncrement(availableScrollWidth, fullScrollWidth) {
		// allow up to 10 steps per page, with a minimum of 1px per step
		const pageIncrement = _getPageIncrement(availableScrollWidth, fullScrollWidth);
		return Math.ceil(pageIncrement / 10);
	}


	const _TableSlider = declare(HorizontalSlider, {
		intermediateChanges: true,
		minimum: 0,

		/** increment to use when arrow clicked (as opposed to page up/down keys) */
		arrowIncrement: 0,

		/** 
		 * @override
		 * Adjust value of slider when page down key or left arrow is clicked 
		 */
		decrement(e) {
			const {arrowIncrement, pageIncrement} = this;
			this._bumpValue(e.keyCode == keys.PAGE_DOWN ? -pageIncrement : -arrowIncrement);
		},

		/** 
		 * @override
		 * Adjust value of slider when page up key or right arrow is clicked 
		 */
		increment(e) {
			const {arrowIncrement, pageIncrement} = this;
			this._bumpValue(e.keyCode == keys.PAGE_UP ? pageIncrement : arrowIncrement);
		},

		/**
		 * @override
		 * Mouse wheel should alter value the same as using the left/right arrows
		 */
		_mouseWheeled(e) {
			if (!this.focused) {
				return;
			}

			e.stopPropagation();
			e.preventDefault();

			if (e.wheelDelta < 0) {
				this.decrement(e);
			} else {
				this.increment(e);
			}
		}
	});

	return declare(Stateful, {
		/** @property {number} bodyWidth - total width of table body (excluding scrollbar and extra column) */
		bodyWidth: 0,

		/** @property {number[]} columnWidths - current widths of columns (in colgroup) */
		columnWidths: null,

		/** @property {number} containerWidth - width of container during freeze operation */
		containerWidth: null,

		/** @property {number} currentHoverHeadColumnIndex - the index of the hovering column on the table head */
		currentHoverHeadColumnIndex: null,

		/** @property {number} footHeight - height of table footer */
		footHeight: 0,

		/** @property {Object[]} frozenIndicatorHandlers - event handlers for the frozen column */
		frozenIndicatorHandlers: null,

		/** @property {number} frozenLeftOffset - width of left column hidden due to scrolling */
		frozenLeftOffset: 0,

		/** @property {number} frozenRightOffset - width of right column hidden due to scrolling */
		frozenRightOffset: 0,

		/** @property {number} frozenScrollWidth - remaining width of table for scrolling columns when frozen */
		frozenScrollWidth: 0,

		/** @property {HorizontalSlider} frozenSlider - slider used to scroll table horizontally */
		frozenSlider: null,

		/** @property {number} headCurrentHeight - height of table header as currently displayed; if hidden, then this is 0 */
		headCurrentHeight: 0,

		/** @property {number} headHeight - height of table header */
		headHeight: 0,

		/** @property {number[]} headerHeights - height of first one or two (if matrix) rows in header */
		headerHeights: null,

		/** @property {number[]} headerWidths - width of each column header */
		headerWidths: null,

		/** @property {boolean} hoveringOnHead - return true if the user is hovering on the table head */
		hoveringOnHead: false,

		/** @property {number} maxHeight - calculated max height of table body */
		maxHeight: 0,

		/** @property {number} maxScrollPosition - max scroll position to bring right-most column into view */
		maxScrollPosition: 0,

		/** @property {number} minPopupWidth - minimum width of popup view; only used in popups where extra column is only used when view is too slim  */
		minPopupWidth: 0,

		/** @property {boolean} mouseOverView - track if mouse is over the view */
		mouseOverView: false,

		/** @property {number} nFrozen - index of frozen column */
		nFrozen: -1,

		/** @property {number} nLeft - index of left scroll column */
		nLeft: -1,

		/** @property {number} nRight - index of right scroll column - if column has colspan, then it is set to index plus colspan minus 1 */
		nRight: -1,

		/** @property {number} nMax - max value for right scroll column */
		nMax: -1,

		/** @property {DOMNode} offscreenTable - offscreen table used to capture row heights */
		offscreenTable: null,

		/** @property {TablePlaceholder} placeholderNode - placeholder for table if currently displayed */
		placeholderNode: null,

		/** @property {any} potentialFrozenColumnHandle - event handle to handle the potential frozen column when mouse move over table header */
		potentialFrozenColumnHandle: null,

		/** @property {any} resizeMoveHandle - event handle to track movement of mouse over table header */
		resizeMoveHandle: null,

		/** @property {DOMNode} resizeNode - absolutely positioned node to handle column resizing */
		resizeNode: null,

		/** @property {number} scrollPosition - number of pixels scrolled from the left (max is maxScrollPosition) */
		scrollPosition: 0,

		/** @property {boolean} showFrozenIndicatorFlag - whether turn on the switch to process the frozen indicator or not */
		showFrozenIndicatorFlag: false,

		/** @property {boolean} trackpadXRecentScroll - flag for is trackpad recently scrolled horizontally */
		trackpadXRecentScroll: false,

		/** @property {number} trackpadXRecentScrollTimer - timer for horizontal table recent scroll logic */
		trackpadXRecentScrollTimer: 0,

		/** @property {number} trackpadXScrollTimer - timer for horizontal table scrolling response rate */
		trackpadXScrollTimer: 0,

		/** @property {number} trackpadXStartScrollValue - initial starting amount that breaks past the horizontal scrolling threshold */
		trackpadXStartScrollValue: 0,

		/** @property {number} trackpadXScrollValue - amount to scroll during each tick in horizontal table scrolling */
		trackpadXScrollValue: 0,

		/** @property {number[]} trackpadYScrollValueHistory - stack holding the vertical scroll history */
		trackpadYScrollValueHistory: [0, 0, 0, 0, 0],

		/** @property {csi/table/Widget} view - view to which scrolling is applied */
		view: null,

		/**
		 * Create new table for fixed header
		 */
		constructor: function(params) {
			// NOTE: argument params is needed to match constructor of inherited class Stateful
			const {view} = params;
			const {tableContainerNode, tableScrollNode} = view;
			const bodyTable = view.getBodyTableNode();
			const bodyTableScrollNode = this.bodyTableScrollNode = bodyTable.parentElement;
			const headTable = view.headTableNode = bodyTable.cloneNode();
			const headTableScrollContainer = html.create("div", {"class": "csi-header-table-scroll"});
			const offscreenTable = this.offscreenTable = bodyTable.cloneNode();

			// split tables for head and body
			html.destroyChildren(view.headNode);
			html.destroyChildren(view.bodyNode);

			view.headNode = html.create("thead", null, headTable);
			html.create("thead", null, offscreenTable);
			html.create("tbody", null, offscreenTable);

			tableScrollNode.insertBefore(headTableScrollContainer, bodyTableScrollNode);
			headTableScrollContainer.appendChild(headTable);
			tableScrollNode.insertBefore(offscreenTable, bodyTableScrollNode);

			offscreenTable.classList.add("csi-offscreen");
			headTable.classList.add("csi-view-table-fixed-head");
			bodyTable.classList.add("csi-view-table-fixed-body");
			tableContainerNode.classList.add("csi-view-table-fixed", "csi-view-table-fixed-to-scroll");

			// setup for column resizing
			this.resizeNode = html.create("div", {
				className: "csi-table-column-resizer"
			}, tableScrollNode);

			view.own(
				on(view.scrollNode, "mouseenter", e => this._onMouseOverView(e)),
				on(view.scrollNode, "mouseleave", e => this._onMouseLeaveView(e)),
				on(this.resizeNode, touch.press, e => this._onResizeStart(e)),
				on(bodyTableScrollNode, "scroll", e => this._onBodyScroll(e)),
				on(headTableScrollContainer, "scroll", e => this._onBodyScroll(e)),
				on(tableScrollNode, "wheel", e => this._onTrackpadScroll(e)),
			);

			view.watch("disabled", (name, oldValue, newValue) => this._onResizeReset(!newValue));

			this.watch("currentHoverHeadColumnIndex", (name, oldValue, newValue) => {
				this._onPotentialFrozenColumnChange(oldValue, newValue);
			});

			if (html.getVerticalScrollbarWidth() === 0) {
				tableScrollNode.classList.add("csi-table-force-scrollbars");
			}
		},

		/**
		 * Fix the table heights once header is rendered and header height is known
		 */
		fixTableHeights: function() {
			const view = this.view;
			const {tableContainerNode, footNode, hideFooter} = view;

			// set current head height based on if it is displayed or not (as in filmstrip view where it can toggle)
			this.headCurrentHeight = view.hideHeader ? 0 : this.headHeight;

			if (footNode) {
				// calculate height of footer
				let footHeight = html.preciseHeight(footNode);

				// in some cases the table is sized before it is visible (such as in wizard), so in such a case reuse the previous
				// footer height; the footer height should be fairly constant, unless browser zoom changes. If the footer
				// height is valid, then save for next time
				if (footHeight) {
					_footHeight = footHeight;
				} else if (!hideFooter) {
					footHeight = _footHeight;
				}

				this.footHeight = footHeight;
			} else {
				this.footHeight = 0;
			}

			// set height of table, header, and footer
			this._calculateMaxHeight();
			html.setCSSVariable(tableContainerNode, "TableHeaderHeight", this.headCurrentHeight, true);
			html.setCSSVariable(tableContainerNode, "TableFooterHeight", this.footHeight, true);
		},

		/**
		 * Freeze rows before being inserted into the table
		 * @param {DOMNode[]} rows
		 * @param {boolean} bHeader
		 */
		fixTableRows: function(rows, bHeader) {
			const {offscreenTable, view} = this;
			const {hasSubheaderRow, headTableNode, memoryTable} = view;
			const cells = [];
			const cellHeights = [];
			const overflowCells = [];
			let headerRowHeight;
			let subheaderRowHeight = 0;

			// insert all rows into appropriate section of off-screen table (thead or tbody)
			const tableSection = bHeader ? offscreenTable.firstChild.nextSibling : offscreenTable.lastChild;

			rows.forEach(row => {
				tableSection.appendChild(row);
			});

			// only calculate heights if table is currently shown (could be in a hidden tab)
			const hasHeight = !!tableSection.clientHeight;
			if (hasHeight) {
				const addCell = (cell) => {
					const h = html.preciseHeight(cell);
					cells.push(cell);
					cellHeights.push(h);
				};

				// capture row heights
				rows.forEach((row) => {
					if (!bHeader) {
						addCell(row);
					}
					const cell = row.firstChild;
					addCell(cell);

					// if row has a matrix (first column has rowspan), then also track height of first matrix cell (first column without a rowspan)
					if (cell.rowSpan > 1) {
						const matrixCell = Array.from(row.children).find((cell) => cell.rowSpan === 1);
						addCell(matrixCell);
					}
				});

				if (bHeader) {
					// track height of matrix subheaders - we use "2" because the first row will track 2 cells when there is a matrix, so "2" will be the first cell of the second row
					if (hasSubheaderRow) {
						subheaderRowHeight = cellHeights[2];
					}

					// track height of first header row, and second if there are matrices, as well as overall height of header;
					// get height of full header from DOM rather than summing the individual heights - could lead to errors
					headerRowHeight = cellHeights[0];
					this.headerHeights = [headerRowHeight, subheaderRowHeight];
					this.headHeight = html.preciseHeight(tableSection);

					// track any table headers that overflow their bounds
					_collectOverflowCells(tableSection, overflowCells);
				}
			}

			// freeze rows
			if (this.isFrozen()) {
				const {nFrozen, nLeft, nRight} = this;

				rows.forEach(row => {
					memoryTable.freezeRow(row, nFrozen, nLeft, nRight);
				});
			}

			// set heights - only do so when not resizing the table column, or after releasing it
			if (hasHeight && (_resizeReleased || !_resizeHandles)) {
				cells.forEach((cell, i) => {
					html.setPixels(cell, html.HEIGHT, cellHeights[i]);
				});
			}

			if (bHeader) {
				if (hasHeight) {
					// set overflow
					_processOverflowCells(overflowCells);

					// cap height of matrix headers so that as they scroll into view, their text does not wrap and increase size of header;
					// NOTE: it is important that this is applied to the head table, and does not affect the offscreen table where calculations are done
					html.setCSSVariable(headTableNode, "TableMatrixGroupMaxHeight", headerRowHeight - subheaderRowHeight, true);
				}
			} else if (this.placeholderNode) {
				// rows are being prepped for table body, so placeholder should be removed
				this.updatePlaceholder();
			}
		},

		/**
		 * Return width available for content in column minus the padding and border
		 * @param {number} columnIndex
		 * @return {number}
		 */
		getAvailableColumnWidth: function(columnIndex) {
			// NOTE: currently only used for image cells, so use image cell padding rather than normal cell padding
			return this.getFixedColumnWidth(columnIndex) - (2 * csiTable.IMAGE_CELL_PADDING) - 1;
		},

		/**
		 * Return fixed width for column
		 * @param {number} columnIndex
		 * @return {number}
		 */
		getFixedColumnWidth: function(columnIndex) {
			return this.headerWidths[columnIndex];
		},

		/**
		 * Return header cell for given column - could be in main header row or subheader row
		 * @param {number} column
		 * @return {DOMNode}
		 */
		getHeaderCell: function(column) {
			const {view} = this;
			return view.getTableCellAtIndex(view.getSubheaderRow(), column) || view.getTableCellAtIndex(view.getHeadRow(), column);
		},

		/**
		 * Return true if table is frozen
		 * @return {boolean}
		 */
		isFrozen: function() {
			return this.nFrozen >= 0;
		},

		/**
		 * Return true if table is scrolling vertically
		 * @return {boolean}
		 */
		isScrolled: function() {
			return true;
		},

		/**
		 * Handle key events for frozen table
		 * @param {Event} e
		 */
		onKey: function(e) {
			if (!this.isFrozen()) {
				return false;
			}

			const ctrlPressed = html.ctrlKeyPressed(e);
			const {
				frozenScrollWidth,
				hoveringOnHead,
				maxScrollPosition,
				mouseOverView,
				potentialFrozenColumnHandle,
				resizeMoveHandle,
				resizeNode,
				scrollPosition,
				showFrozenIndicatorFlag,
			} = this;

			if (ctrlPressed) {
				// show the frozen column indicator and potential frozen column indicator
				if (hoveringOnHead) {
					this._setFrozenIndicator(true);
					const potentialFrozenColumnIndex = this.get("currentHoverHeadColumnIndex");
					this._setTableHeadFrozenColumnStyling(null, COLUMN_INDICATOR_KEY_POTENTIAL, potentialFrozenColumnIndex);
					html.hide(resizeNode);
				}

				this._processFrozenIndicator(e);
			}

			if (showFrozenIndicatorFlag) {
				resizeMoveHandle.pause();
				potentialFrozenColumnHandle.resume();
			}

			// if mouse is over frozen table view, then trap left/right arrows and scroll horizontally
			if (mouseOverView && !focus.curNode) {
				const bLeft = (e.keyCode == keys.LEFT_ARROW);
				const bRight = (e.keyCode == keys.RIGHT_ARROW);

				if (bLeft || bRight) {
					let pos;

					if (ctrlPressed) {
						// scroll to left or right border if CTRL key used
						pos = bLeft ? 0 : maxScrollPosition;
					} else {
						// scroll left or right by delta
						const stepIncrement = _getStepIncrement(frozenScrollWidth, maxScrollPosition);
						pos = scrollPosition + stepIncrement * (bLeft ? -1 : 1);
					}

					this._throttleScroll(pos);
					return true;
				}
			}

			return false;
		},

		/**
		 * @override
		 * Remove the indicators when release the ctrl key
		 */
		onKeyRelease: function(e) {
			const {
				showFrozenIndicatorFlag,
				resizeMoveHandle,
				potentialFrozenColumnHandle,
				resizeNode
			} = this;

			// ctrl key was pressed before
			if (showFrozenIndicatorFlag) {
				// hide the frozen column indicator and potential frozen column indicator
				this._setFrozenIndicator(false);
				const potentialFrozenColumnIndex = this.get("currentHoverHeadColumnIndex");
				this._setTableHeadFrozenColumnStyling(null, COLUMN_INDICATOR_KEY_FROZEN, potentialFrozenColumnIndex);
				html.show(resizeNode);

				if (resizeMoveHandle) {
					resizeMoveHandle.resume();
					potentialFrozenColumnHandle.pause();
				}

				this._processFrozenIndicator(e);
			}
		},

		/**
		 * Refresh heights of table header if necessary
		 */
		refreshHeader: function() {
			const {headCurrentHeight, nFrozen, nLeft, nRight, view} = this;
			const {hideHeader, headNode, memoryTable} = view;
			const height = html.preciseHeight(headNode);

			if (hideHeader || !height) {
				return;
			}

			// if header is not the same as before, nothing to do -- unless finishing resize operation
			if (!_resizeReleased && (Math.abs(height - headCurrentHeight) <= 1)) {
				return;
			}

			// restore header rows so they can be re-frozen at appropriate heights
			const rows = [...headNode.children];

			if (this.isFrozen()) {
				rows.forEach(row => {
					memoryTable.unfreezeRow(row, nFrozen, nLeft, nRight);
				});
			}

			rows.forEach(row => {
				html.setPixels(row.firstChild, html.HEIGHT, "");
			});

			this.fixTableRows(rows, true);
			rows.forEach(row => headNode.appendChild(row));
			this.fixTableHeights();
		},

		/**
		 * Refresh height of given row; if it is a matrix row, then need to refresh it and its parent row
		 * @param {DOMNode} row
		 */
		refreshRow: function(row) {
			const baseRow = csiTable.getBaseMergedRow(row);
			const rows = baseRow ? csiTable.getMergedRows(baseRow) : [row];
			this.refreshRows(rows);
		},

		/**
		 * Refresh height of rows
		 * @param {DOMNode[]} [rows]
		 */
		refreshRows: function(rows) {
			const {offscreenTable, view} = this;
			const frozen = this.isFrozen();
			const offscreenBody = offscreenTable.lastChild;

			rows = rows || view.bodyNode.childNodes;

			// extract first cell of each row
			const cells = _.map(rows, (row) => row.firstChild);

			// if there are frozen columns, then need to render whole row offscreen to get height of full row
			let clonedCells;

			if (frozen) {
				clonedCells = _.map(rows, (row) => {
					row = row.cloneNode(true);
					offscreenBody.appendChild(row);
					return row.firstChild;
				});
			} else {
				clonedCells = cells;
			}

			// extract height for each row
			const cellHeights = clonedCells.map((cell) => html.preciseHeight(cell));

			// apply heights to rows
			cells.forEach((cell, i) => {
				html.setPixels(cell, html.HEIGHT, cellHeights[i]);
			});

			// clear offscreen table of cloned rows
			if (frozen) {
				clonedCells.forEach((cell) => {
					offscreenBody.removeChild(cell.parentNode);
				});
			}
		},

		/**
		 * Recalculate available height and width of table
		 */
		resize: function() {
			const view = this.view;

			// if no header at all, in that case, just return, for example, the "contentMessage" get populated, the table is not supposed to rendered
			if (!this.headerWidths) {
				return;
			}

			// for popup views (and maybe others), if DOM was not visible when fixTableHeights called first time, then call now
			const hasHeight = !!this.headCurrentHeight;
			const shouldHaveHeight = !view.hideHeader;

			if (hasHeight != shouldHaveHeight) {
				this.fixTableHeights();
			} else {
				this._calculateMaxHeight();
			}

			// update container widths and resize tables accordingly
			this._calculateContainerWidth();
			this._resizeWidth();

			// if parent view is table, then resize it as well as this one may have changed the size of the dialog
			const parentScrollData = view.bPopup && view.viewParent && view.viewParent.scrollData;
			if (parentScrollData) {
				parentScrollData.resize();
			}
		},

		/**
		 * Scroll table horizontally so that node at the given index is in view
		 * @param {DOMNode} cell
		 */
		scrollHorizontalToCell: function(cell) {
			if (!this.isFrozen()) {
				return;
			}

			const column = this.view.getTableCellIndex(cell);
			if (column <= this.nFrozen) {
				return;
			}

			// only scroll if the column is currently not displayed
			const columnMin = this._getColumnPosition(column);
			const columnMax = columnMin + this.headerWidths[column];
			const scrollMin = this.scrollPosition;
			const scrollMax = scrollMin + this.frozenScrollWidth;
			const bLeft = columnMin < scrollMin;
			const bRight = columnMax > scrollMax;

			if (bRight) {
				// if scrolling to the right, then align column to right border rather than left
				this._scroll(columnMax - this.frozenScrollWidth);
			} else if (bLeft) {
				this._scroll(columnMin);
			}
		},

		/**
		 * Initialize tables with header and body widths
		 * @param {number[]} headerWidths
		 * @param {number} bodyWidth
		 */
		setHeaderWidths: function(headerWidths, bodyWidth) {
			this.bodyWidth = bodyWidth;
			this.headerWidths = headerWidths;
			this._calculateContainerWidth();
			csiTable.fixTableWidths(this.offscreenTable, this.bodyWidth, this.headerWidths);
			this._resizeWidth();
		},

		/**
		 * Make the given header appear as the first cell after the left border when the table is frozen
		 * @param {number} column
		 */
		setLeftBorder: function(column) {
			if (this.isFrozen() && (column >= this.nLeft) && (column <= this.nRight)) {
				const columnMin = this._getColumnPosition(column);
				const pos = Math.min(columnMin, this.maxScrollPosition);
				this._scroll(pos);
			}
		},

		/**
		 * Reset view reference so no circular reference
		 */
		uninitialize: function() {
			delete this.view;
		},

		/**
		 * Update header table and action button "More" position
		 */
		updateBodyScroll: function() {
			this._onBodyScroll();
		},

		/**
		 * Update visibility of table placeholder
		 */
		updatePlaceholder: function() {
			const MINIMUM_HEIGHT_FOR_IMAGE = 300;
			const view = this.view;
			const hasResults = view.hasResultRows();
			const suspended = view.isSuspended();
			const {
				bPopup,
				bodyNode,
				bodyTableNode,
				containerNode,
				footNode,
				relRoot,
				showCountIfSuspended,
				suspendedUntilScrolledIntoView,
				viewParent,
			} = view;

			const classList = containerNode.classList;
			let description;
			let hasColumnFilter;
			let hasNewAction;
			let showActions = false;
			let titleText;
			let type = view._getNoResultType();

			// parameters for placeholder resource are target type of view and root type of view
			const params = [
				locale.type(view.getResultNodeType()),
				locale.type(view.getViewNodeType())
			];

			// remove existing placeholder
			if (this.placeholderNode) {
				this.placeholderNode.destroy();
				this.placeholderNode = null;
			}

			if (suspendedUntilScrolledIntoView) {
				titleText = locale.resource("LoadingEllipsis");
				description = "";
			} else if (!hasResults) {
				// Regular "No Result" case - show placeholder
				// override functions _getNoResultType() and _getNoResultDescription() to introduce non standard placeholder
				hasColumnFilter = view._hasColumnFilter();
				hasNewAction = relRoot && relRoot.hasNewAction();
				if (suspended) {
					// Table is suspended - show placeholder
					type = "Suspend";
					description = locale.resource("ViewPlaceholderDescription" + type, params)
				} else {
					description = view._getNoResultDescription(hasColumnFilter, hasNewAction);
				}
				showActions = true;

				// ensure any action rows in view are removed
				if (hasNewAction) {
					view._deleteChildren();
				}
			} else {
				html.setPixels(bodyTableNode, html.HEIGHT, "");
				classList.remove(CSS_PLACEHOLDER, CSS_PLACEHOLDER_NO_FILTER, CSS_PLACEHOLDER_NO_FOOTER);
				return;
			}

			// determine if table footer should be shown or not
			if (footNode) {
				const showCount = suspended && showCountIfSuspended && !suspendedUntilScrolledIntoView;

				if (showCount || this.isFrozen()) {
					if (!showCount) {
						view.set("message", "");
					}

					classList.remove(CSS_PLACEHOLDER_NO_FOOTER);
				} else {
					classList.add(CSS_PLACEHOLDER_NO_FOOTER);
				}
			}

			if (!suspended && !hasColumnFilter) {
				classList.add(CSS_PLACEHOLDER_NO_FILTER);
			}

			classList.add(CSS_PLACEHOLDER);

			// generate placeholder in the table
			const tableHeight = view._getEffectiveTableHeight();
			let height = "";
			let bImage = true;

			if (!csiTable.isAutomaticHeight(tableHeight) || this.maxHeight) {
				height = bPopup ? MINIMUM_HEIGHT_FOR_IMAGE : (this.maxHeight || csiTable.getMinimumHeight());
				height -= this.useNaturalHorizontalScrolling() ? this.headCurrentHeight : 0;

				if (this.useNaturalHorizontalScrolling()) {
					// Allow vertical space for horizontal scroll bar
					height -= html.getHorizontalScrollbarHeight();
				}
				bImage = (height >= MINIMUM_HEIGHT_FOR_IMAGE) && (!viewParent || !viewParent.isStackedView());
			}

			html.setPixels(bodyTableNode, html.HEIGHT, height);

			titleText = titleText || locale.resource("ViewPlaceholderTitle" + type, params);

			this.placeholderNode = new view.placeholderClass({
				bImage,
				columns: view.numberOfColumns,	// use full number of columns even when frozen - width is set to 100% so no overflow
				description,
				showActions,
				titleText,
				type,
				widget: view
			}).placeAt(bodyNode, "before");
		},

		/**
		 * Calculate available width of table by first forcing it to be as wide as all its columns; if it overflows,
		 * the scroll node will cut it off and be the max width available
		 * @param {number} desiredWidth
		 * @return {number}
		 */
		_calculateAvailableWidth(desiredWidth) {
			const {view: {scrollNode}} = this;
			let width = html.preciseWidth(scrollNode);

			if (width >= desiredWidth) {
				return desiredWidth;
			}

			// add element with body width to force scroll node to expand to the maximum allowed space
			const div = html.create("div", {
				style: {width: `${desiredWidth}px`}
			}, scrollNode);

			width = html.preciseWidth(scrollNode);
			html.orphan(div);

			return width;
		},

		/**
		 * Calculate width available for table body; table always shows scrollbar, so subtract its width along with 3 pixels to
		 * account for left-right borders on table container node and 1px padding on table scroll node
		 */
		_calculateContainerWidth: function() {
			const {bodyWidth, view} = this;
			const {bPopup, inReactDialog} = view;
			const offset = csiTable.getTableScrollbarWidth() + 3;
			const currentViewWidth = view.getAvailableTableWidth(true);
			const fullWidth = inReactDialog ? this._calculateAvailableWidth(bodyWidth + offset) : view.getAvailableTableWidth();
			const availableWidth = Math.floor(fullWidth - offset);

			this.containerWidth = (bPopup || !this.useNaturalHorizontalScrolling()) ? availableWidth : Math.max(availableWidth, bodyWidth);
			this.minPopupWidth = bPopup ? Math.ceil(currentViewWidth - offset) : 0;
		},

		/**
		 * Calculate available height of table body and apply it to table;
		 * if focused or flex height, then container determines the max-height
		 */
		_calculateMaxHeight: function() {
			const {headCurrentHeight, footHeight, view} = this;
			const tableHeight = view._getEffectiveTableHeight();
			const bInSwitchboard = view.bInSwitchboard;
			let h;

			if (!csiTable.isAutomaticHeight(tableHeight)) {
				h = csiTable.getFixedHeight(tableHeight) || 0;
			} else if (!view.bFocused && !view.flexHeight) {
				h = Math.max(view.getAvailableTableHeight() - footHeight - 2, csiTable.getMinimumHeight());
				h -= this.useNaturalHorizontalScrolling() ? 0 : headCurrentHeight;
			}

			// limit minimum height of second switchboard table to TABLE_HEIGHT_SMALL
			if (bInSwitchboard) {
				const minHeight = csiTable.getFixedHeight(constants.TABLE_HEIGHT_SMALL);
				if (h < minHeight) {
					h = minHeight;
				}
			}
			this.maxHeight = h;

			// if no max height, then let table be as tall as it wants
			html.setCSSVariable(view.tableScrollNode, "TableFullHeight", h || "", true);
		},

		/**
		 * Calculate and return new frozen column
		 * @return {number}
		 */
		_calculateFreezeColumn: function() {
			const {bodyWidth, containerWidth, headerWidths, view} = this;
			const offset = view.allowsSelections() ? 1 : 0;
			const frozenColumnSetting = view.get("tableFreezeColumns");
			const minColumnsToFreeze = 2 + offset;

			// do not freeze table if using natural horizontal scrolling
			if (this.useNaturalHorizontalScrolling()) {
				return -1;
			}

			if ((bodyWidth > containerWidth) && (view.numberOfDataColumns >= minColumnsToFreeze)) {
				// calculate column to freeze - if set to 1, then frozen column index is 0;
				// if table has selections column though, then need to add one back
				let nFrozen = Math.max(frozenColumnSetting, 1) - 1 + offset;

				if (nFrozen < 0) {
					// freeze first data column
					nFrozen = offset;
				} else {
					// ensure that frozen column is not part of a matrix and within the width limit
					const frozenWidthRatio = 0.66;
					const maxFrozenWidth = Math.round(containerWidth * frozenWidthRatio);
					let nValid = -1;
					let w = 0;

					view.columnData.traverse(true, function(c, sc, i, n) {
						w += headerWidths[n];

						if ((n <= nFrozen) && (w <= maxFrozenWidth) && !c.columnUrl) {
							// if a non-column matrix, only the last column can be frozen
							if (!sc || (sc === c.getLastSub())) {
								nValid = n;
							}
						}
					});

					nFrozen = Math.max(nValid, offset);
				}

				return nFrozen;
			}

			return -1;
		},

		/**
		 * Calculate and store width available to scroll horizontally
		 */
		_calculateFrozenWidth: function() {
			const headerWidths = this.headerWidths;
			let w = 0;

			for (let i = 0; i <= this.nFrozen; ++i) {
				w += headerWidths[i];
			}

			if (this._hasRowActions()) {
				w += headerWidths[this.nMax + 1];
			}

			this.frozenScrollWidth = this.containerWidth - w;
			this.maxScrollPosition = this.bodyWidth - this.containerWidth;
		},

		/**
		 * Track the hover column
		 * @param {Event} e
		 */
		_frozenEventMouseMoveOnHead: function(e) {
			this.hoveringOnHead = true;

			if (!this.isFrozen()) {
				return;
			}

			if (html.ctrlKeyPressed(e)) {
				const index = this.view.getHeaderElementMaxIndex(e.target);
				if (index >= 0) {
					this.set("currentHoverHeadColumnIndex", index);
					this._setTableHeadFrozenColumnStyling(e, COLUMN_INDICATOR_KEY_POTENTIAL);
				}
			}
		},

		/**
		 * Return position of column relative to the frozen column
		 * @param {number} column
		 * @return {number}
		 */
		_getColumnPosition: function(column) {
			const headerWidths = this.headerWidths;
			let pos = 0;

			for (let i = this.nFrozen + 1; i < column; ++i) {
				pos += headerWidths[i];
			}

			return pos;
		},

		/**
		 * Return position of column in frozen table (rather than original table)
		 * @param {number} column
		 */
		_getCurrentColumnPosition: function(column) {
			const {columnWidths, nFrozen, nLeft, nRight, nMax} = this;
			let pos = 0;

			// subtract missing columns
			if (this.isFrozen()) {
				if (column > nLeft) {
					column -= nLeft - nFrozen - 1;
				}

				if (column > nMax) {
					column -= nMax - nRight;
				}
			}

			for (let i = 0; i < column; ++i) {
				pos += columnWidths[i];
			}

			return pos;
		},

		/**
		 * Return true if table has actions column
		 * @return {boolean}
		 */
		_hasRowActions: function() {
			return this.view.get("hasQueryResultActions");
		},

		/**
		 * Update scroll position of head table when body table scrolls
		 */
		_onBodyScroll: function(e) {
			const {bodyTableScrollNode, view: {headingsNode, headTableNode, tableContainerNode}} = this;
			const headTableScrollNode = headTableNode.parentElement;
			const bNaturalScroll = this.useNaturalHorizontalScrolling();
			if (e) {
				if (e.target == bodyTableScrollNode) {
					headTableScrollNode.scrollLeft = bNaturalScroll ? bodyTableScrollNode.scrollLeft : 0;
				} else {
					bodyTableScrollNode.scrollLeft = bNaturalScroll ? headTableScrollNode.scrollLeft : 0;
				}
			}

			// Center Action Row Button (aka button "More")
			const tableMoreButton = headingsNode.querySelector(".csi-table-row-more .csi-table-natural-scroll-more");
			const tableActionsButton = headingsNode.querySelector(".csi-table-row-actions .csi-table-natural-scroll-more");
			if (bNaturalScroll) {
				if (tableMoreButton) {
					const tableActionBtnWidth = tableMoreButton.offsetWidth;
					const margin = (tableContainerNode.offsetWidth - tableActionBtnWidth) / 2 + bodyTableScrollNode.scrollLeft;
					html.setCSSVariable(tableContainerNode, "TableBtnMoreMarginLeft", margin, true);
				}
				if (tableActionsButton) {
					html.setCSSVariable(tableContainerNode, "TableBtnMoreMarginLeft", bodyTableScrollNode.scrollLeft + 25, true);
				}
			}
		},

		/**
		 * Mouse is leaving view
		 * @param {Event} e
		 */
		_onMouseLeaveView: function(e) {
			document.body.parentElement.classList.remove(constants.TABLE_NO_SWIPE_TO_GO_BACK);
			this.mouseOverView = false;
		},

		/**
		 * Mouse is entering view
		 * @param {Event} e
		 */
		_onMouseOverView: function(e) {
			document.body.parentElement.classList.add(constants.TABLE_NO_SWIPE_TO_GO_BACK);
			this.mouseOverView = true;
		},

		/**
		 * Update scroll position of head table when trackpad scrolls
		 * @param {Event} e
		 */
		_onTrackpadScroll: function(e) {

			// if scrolling is natural, skip this logic
			if (this.useNaturalHorizontalScrolling()) {
				return;
			}

			const {
				bodyWidth,
				containerWidth,
				maxScrollPosition,
				scrollPosition,
				view: {bodyNode},
			} = this;
			const currentTime = Date.now();
			const {deltaX, deltaY} = e;
			const isScrollingHorizontal = Math.abs(deltaX / deltaY) > 1;

			// if scrolling is immediately horizontal, prevent browsers default behavior to disable swipe to go back / forward
			if (isScrollingHorizontal) {
				e.preventDefault();
			}

			// if X scrolling was not recent, capture X scrolling into a starting value
			if (!this.trackpadXRecentScroll) {
				this.trackpadXStartScrollValue += deltaX;
			}

			// track the history of Y scrolling
			this.trackpadYScrollValueHistory.unshift(deltaY);
			this.trackpadYScrollValueHistory.pop();
			const trackpadYScrollValueHistorySum = this.trackpadYScrollValueHistory.reduce((partialSum, a) => partialSum + a, 0);

			// if the history of Y scrolling was large enough, reset X scroll start value
			if (Math.abs(trackpadYScrollValueHistorySum) > TRACKPAD_Y_RESET_X_THRESHOLD) {
				this.trackpadXStartScrollValue = 0;
			}

			// check proper width, and that the user has scrolled past the threshold to begin
			if ((!this.trackpadXRecentScroll &&
				Math.abs(this.trackpadXStartScrollValue) < TRACKPAD_X_SCROLL_THRESHOLD) ||
				!bodyWidth ||
				!containerWidth ||
				bodyWidth <= containerWidth
			) {
				return;
			}

			// calculate the trackpad response rate (slower for bigger tables, faster for smaller)
			const TRACKPAD_X_RESPONSE_RATE = (
				Math.min(
					Math.max(
						bodyNode.childElementCount + TRACKPAD_X_RESPONSE_RATE_OFFSET,
						TRACKPAD_X_RESPONSE_RATE_MAX,
					),
					TRACKPAD_X_RESPONSE_RATE_MIN,
				)
			);

			// capture X scrolling
			this.trackpadXScrollValue += deltaX;

			// if the timer has reached the trackpad response rate, begin a scroll event
			if (currentTime - this.trackpadXScrollTimer > TRACKPAD_X_RESPONSE_RATE) {

				// reset the time to the current time
				this.trackpadXScrollTimer = currentTime;
				// add the starting X scroll value to the X scroll value
				this.trackpadXScrollValue += this.trackpadXStartScrollValue;

				// if the user is near the end and has scrolled enough to reach it, go directly to the end
				// likewise, if the user is near the beginning and has scrolled enough to reach it, go there
				// otherwise, go to the current scroll position plus the trackpad X scroll value
				if (this.trackpadXScrollValue > 0 && maxScrollPosition - scrollPosition < this.trackpadXScrollValue) {
					this._scroll(maxScrollPosition);
				} else if (this.trackpadXScrollValue < 0 && Math.abs(this.trackpadXScrollValue) > scrollPosition) {
					this._scroll(0);
				} else {
					this._scroll(scrollPosition + this.trackpadXScrollValue);
				}

				// reset scroll values after each scroll event
				this.trackpadXScrollValue = this.trackpadXStartScrollValue = 0;

				// indicate a recent X scroll
				this.trackpadXRecentScroll = true;

				// add a timer to indicate X has not been scrolled after TRACKPAD_X_RECENT_SCROLL_TIMEOUT amount of time
				clearTimeout(this.trackpadXRecentScrollTimer);
				this.trackpadXRecentScrollTimer = setTimeout(() => {
					this.trackpadXRecentScroll = false;
				}, TRACKPAD_X_RECENT_SCROLL_TIMEOUT);
			}
		},

		/**
		 * Clear out the index
		 */
		_onHeadMouseLeave: function(e) {
			this.set("currentHoverHeadColumnIndex", null);
		},

		/**
		 * Apply the freezing styling, hide the potential styling
		 */
		_onHeadMousePress: function(e) {
			this._setTableHeadFrozenColumnStyling(e, COLUMN_INDICATOR_KEY_FREEZEING);
		},

		/**
		 * Clear out the styling
		 */
		_onHeadMouseRelease: function(e) {
			this._setTableHeadFrozenColumnStyling(e, COLUMN_INDICATOR_KEY_FROZEN);
		},

		/**
		 * Place resize node over nearest column border when moved over table head
		 * @param {Event} e
		 */
		_onMouseMoveOverHead: function(e) {
			const {
				frozenRightOffset,
				headerHeights,
				nRight,
				resizeNode,
				view
			} = this;

			// force this view to be last action view so that key event can be received immediately
			state.set("lastActionView", view);
			this.hoveringOnHead = true;

			const cell = view.getCell(e.target, true);
			const hide = () => html.hide(resizeNode);

			// if cell not found, any buttons are pressed, or the view is not accepting mouse clicks, then hide
			if (!cell || !!e.buttons || view.disableOnClick || view.isLoadingView()) {
				hide();
				return;
			}

			const row = cell.parentNode;
			const rowIndex = html.getElementIndex(row);
			const maxRowIndex = view.hasSubheaderRow ? 1 : 0;

			// determine if it is over the main header or subheader row; if not, then hide
			if (rowIndex > maxRowIndex) {
				hide();
				return;
			}

			// determine if it is over first or second half of header to see where to place it (left or right side);
			// if first column, then must be right side
			let column = view.getTableCellIndex(cell);
			const horizontalScrollPos = (this.useNaturalHorizontalScrolling() ? view.naturalScrollNode : view.headingsNode).scrollLeft;
			const halfWidth = this.headerWidths[column] / 2 + horizontalScrollPos;
			const columnOffset = e.offsetX + horizontalScrollPos;

			if ((column < 0) || (column >= view.numberOfDataColumns)) {
				// position to right of last data column
				column = view.numberOfDataColumns - 1;
			} else if (columnOffset < halfWidth) {
				// over left part of column, so move resize node to previous column
				--column;
			}

			// if last column in frozen table, can only resize if fully in view
			if (frozenRightOffset && (column === nRight)) {
				hide();
				return;
			}

			// ensure column can be resized, which does not include the selection column
			column = Math.max(column, view.allowsSelections() ? 1 : 0);

			// make sure there is a key for this column where to store the result
			const spec = view.columnData.getByIndex(column, true);
			const columnKey = spec.getColumnWidthKey();

			if (!columnKey) {
				hide();
				return;
			}

			// if cell spans multiple columns (matrix group), then disallow
			if (rowIndex === 0) {
				const cellToChange = view.getTableCellAtIndex(row, column);

				if (view.cellSpansMultipleColumns(cellToChange)) {
					hide();
					return;
				}
			}

			// calculate right side of column
			const pos = this._getCurrentColumnPosition(column + 1) - horizontalScrollPos;
			const top = rowIndex ? (headerHeights[0] - headerHeights[1]) : 0;

			html.setPixels(resizeNode, html.LEFT, pos - RESIZE_NODE_HALF_WIDTH);
			html.setPixels(resizeNode, html.TOP, top);
			html.setPixels(resizeNode, html.HEIGHT, headerHeights[rowIndex]);
			html.show(resizeNode);
			_resizeColumn = column;
		},

		/**
		 * Remove the styling from the previous and remove the previous frozen column indicator as needed
		 */
		_onPotentialFrozenColumnChange: function(oldValue, newValue) {
			// Due to the event touch.move bubbling up, event mouseout doesn't work that way, handle the tooltip here
			const {
				warningTooltip
			} = this.view;

			if (warningTooltip) {
				warningTooltip.close();
				warningTooltip.destroy();
				this.view.warningTooltip = null;
			}

			if (oldValue != undefined && oldValue != newValue) {
				this._setTableHeadFrozenColumnStyling(null, COLUMN_INDICATOR_KEY_FROZEN, oldValue);
			}

			if (newValue === null) {
				// in the case of leave the head
				this._setFrozenIndicator(false);
				this.hoveringOnHead = false;
			} else {
				// cover the case where the user leave the table head and with the key pressed and then hover again
				this._setFrozenIndicator(true);
			}
		},

		/**
		 * Resize column to new mouse position at most once every 50ms
		 * @param {Event} e
		 */
		_onResizeDrag: function(e) {
			const {COLUMN_WIDTH_MAX} = types;

			const {
				frozenLeftOffset,
				headerWidths,
				maxScrollPosition,
				nLeft,
				nRight,
				scrollPosition
			} = this;

			const x = e.clientX;
			const lastX = _resizeLastX;
			let minColumnWidth = 30;
			let leftColumnHiddenWidth = 0;

			_resizeLastX = x;

			if (this.isFrozen()) {
				const rightCell = this.getHeaderCell(nRight);
				const rightBorderX = rightCell.getBoundingClientRect().right;
				const overflow = x - rightBorderX;

				// if moving leftmost column, make sure visible part of column does not go below min column width
				if (_resizeColumn === nLeft) {
					leftColumnHiddenWidth = frozenLeftOffset;
					minColumnWidth = Math.min(_resizeOriginalColumnWidth, frozenLeftOffset + minColumnWidth);
				}

				if (overflow > 0) {
					// determine delta to apply on a timer to increase or decrease column width
					_resizeTimerDelta = (Math.abs(overflow) > 20) ? 10 : 5;

					// start timer if not already started; if started, then do nothing and wait for timer to fire
					if (!_resizeTimerHandle) {
						_resizeNewColumnWidth = headerWidths[_resizeColumn];
						_resizeTimerHandle = setInterval(() => {
							const delta = Math.min(_resizeTimerDelta, COLUMN_WIDTH_MAX - _resizeNewColumnWidth);

							if (delta > 0) {
								// if increasing column width and mouse is to the right of the scrollable area,
								// then max scroll position will increase, and scroll position must go with it
								this.scrollPosition += delta;
								_resizeNewColumnWidth += delta;
								this._resizeHeader();
							}
						}, SCROLL_TIMEOUT);
					}

					return;
				}
			}

			if (_resizeTimerHandle) {
				// stop timer and start decreasing size of column
				clearTimeout(_resizeTimerHandle);
				_resizeTimerHandle = null;
			}

			// we need to get the "x" position on each drag as it is possible that it changes as the frozen scroll position changes
			const delta = x - lastX;
			let w;

			if (!leftColumnHiddenWidth && (delta < 0) && (scrollPosition == maxScrollPosition)) {
				w = _resizeNewColumnWidth + delta;
			} else {
				w = leftColumnHiddenWidth + x - html.getBoundingRectAndPosition(_resizeColumnCell).x;
			}

			_resizeNewColumnWidth = Math.max(Math.min(Math.round(w), COLUMN_WIDTH_MAX), minColumnWidth);
			this._resizeHeader();
		},

		/**
		 * Stop resizing and reset column to original width if escape is pressed
		 * @param {Event} e
		 */
		_onResizeKey: function(e) {
			if (e.keyCode == keys.ESCAPE) {
				_resizeNewColumnWidth = _resizeOriginalColumnWidth;
				_resizeReleased = true;
				this._resizeHeader();
				this._resizeStop();
				event.stop(e);
			}
		},

		/**
		 * Process one last resize to current mouse position and stop resizing
		 * @param {Event} e
		 */
		_onResizeRelease: function(e) {
			const {view} = this;

			if (_resizeNewColumnWidth !== _resizeOriginalColumnWidth) {
				// update body row heights as the content may have wrapped more - the header is handled below
				this.refreshRows();

				// persist change to column
				view.setFixedColumnWidth(_resizeColumn, _resizeNewColumnWidth);
			}

			_resizeReleased = true;
			this.containerWidth = _resizeOriginalContainerWidth;
			this._resizeHeader(true);
			this._resizeStop();
			event.stop(e);
		},

		/**
		 * Enable or disable column resizing
		 * @param {boolean} enable
		 */
		_onResizeReset: function(enable) {
			const {resizeMoveHandle, resizeNode, view} = this;

			// if no place to save the resized width, then don't allow to resize in the first place
			if (!view.hasCustomViewOption(PROP_FIXEDCOLUMNWIDTHS) || view.isPreview) {
				enable = false;
			}

			if (!enable && resizeMoveHandle) {
				resizeMoveHandle.remove();
				this.resizeMoveHandle = null;
				html.hide(resizeNode);
			} else if (enable && !resizeMoveHandle) {
				this.resizeMoveHandle = on.pausable(view.headNode, touch.move, (e) => this._onMouseMoveOverHead(e));
				this.potentialFrozenColumnHandle = on.pausable(view.headNode, touch.move, (e) => this._frozenEventMouseMoveOnHead(e));
				view.own(
					on(view.headTableNode, touch.leave, (e) => this._onHeadMouseLeave(e)),
					this.resizeMoveHandle,
					this.potentialFrozenColumnHandle
				);
			}
		},

		/**
		 * Prepare for dragging column resize node
		 * @param {Event} e
		 */
		_onResizeStart: function(e) {
			const {
				columnWidths,
				containerWidth,
				headerWidths,
				view
			} = this;

			this._onResizeReset(false);
			this._resizeStyling(true);
			csiAction.stopNonModalAction();

			// do not let overall table width change while resizing a column (especailly in popups),
			// so reset containerWidth to that of existing table (does not include last column over scrollbar)
			const columnsWithoutLast = [...columnWidths];
			columnsWithoutLast.pop();
			this.containerWidth = columnsWithoutLast.reduce((memo, w) => memo + w, 0);

			_resizeColumnCell = this.getHeaderCell(_resizeColumn);
			_resizeLastX = e.clientX;
			_resizeOriginalContainerWidth = containerWidth;
			_resizeOriginalColumnWidth = _resizeNewColumnWidth = headerWidths[_resizeColumn];
			_resizeThrottle = _.throttle(e => this._onResizeDrag(e), RESIZE_TIMEOUT);
			_resizeHandles = [
				on(document, touch.move, _resizeThrottle),
				on(document, touch.release, e => this._onResizeRelease(e)),
				on(document, "keydown", e => this._onResizeKey(e)),
				on(view.domNode, "keydown", e => this._onResizeKey(e)),
			];

			event.stop(e);
		},

		/**
		 * Handle key events for frozen column indicator
		 * @param {Event} e
		 */
		_processFrozenIndicator: function(e) {
			const {
				headTableNode
			} = this.view;

			this.showFrozenIndicatorFlag = html.ctrlKeyPressed(e);

			if (this.showFrozenIndicatorFlag && !this.frozenIndicatorHandlers) {
				this.frozenIndicatorHandlers = [
					on(headTableNode, touch.press, (e) => this._onHeadMousePress(e)),
					on(headTableNode, touch.release, (e) => this._onHeadMouseRelease(e)),
				];
			} else if (!this.showFrozenIndicatorFlag && this.frozenIndicatorHandlers) {
				this.frozenIndicatorHandlers.forEach((handler) => handler.remove());
				this.frozenIndicatorHandlers = null;
			}
		},

		/**
		 * Change resize column size to given width
		 * @param {boolean} applyFinalChanges
		 */
		_resizeHeader: function(applyFinalChanges) {
			console.assert(_resizeColumn != null);

			const {headerWidths, view} = this;
			const oldWidth = headerWidths[_resizeColumn];

			// while dragging, do nothing if width did not change
			if (!_resizeReleased && (_resizeNewColumnWidth === oldWidth)) {
				return;
			}

			const columns = [];
			let delta = 0;

			const updateColumn = (column) => {
				columns.push(column);
				delta += _resizeNewColumnWidth - headerWidths[column];
				headerWidths[column] = _resizeNewColumnWidth;
			};

			if (applyFinalChanges) {
				// in final resize after dragging, see if the column changed is part of a column matrix
				// whose width will apply to other columns
				const specs = view.columnData;
				const spec = specs.getByIndex(_resizeColumn, true);
				const columnKey = spec.getColumnWidthKey();

				specs.traverse(true, (c, sc, i, n) => {
					c = sc || c;

					if (c.key && (c.getColumnWidthKey() === columnKey)) {
						updateColumn(n);
					}
				});
			} else {
				// while dragging, just update single column
				updateColumn(_resizeColumn)
			}

			// apply changes to table
			this.bodyWidth += delta;
			csiTable.fixTableWidths(this.offscreenTable, this.bodyWidth, headerWidths);
			this._resizeWidth();

			// update overflow for column headers
			const overflowCells = [];

			_.each(view.headNode.children, row => {
				_.each(columns, (column) => {
					const th = view.getTableCellAtIndex(row, column);
					if (th) {
						_collectOverflowCells(th, overflowCells);
					}
				});
			});

			_processOverflowCells(overflowCells);
		},

		/**
		 * Release drag handles and prepare for new column to be selected for resize
		 */
		_resizeStop: function() {
			this._resizeStyling(false);

			// prevent race condition by cancelling last throttled resize
			_resizeThrottle.cancel();
			_resizeThrottle = null;

			clearTimeout(_resizeTimerHandle);
			_resizeTimerHandle = null;

			_resizeHandles.forEach(h => h.remove());
			_resizeHandles = null;
			_resizeColumn = null;
			_resizeReleased = false;

			this._onResizeReset(true);

			this.resize();
		},

		/**
		 * Add or remove styling necessary for column resizing. We highlight the header cells using its border
		 * rather than using the pseudo element used for the rest of the table body. The reason is that the header
		 * heights can change when resizing a matrix column, and figuring out the top position of the column
		 * is difficult.
		 * @param {boolean} add
		 */
		_resizeStyling: function(add) {
			const {nMax, nRight, view} = this;
			const {headNode, memoryTable, tableScrollNode} = view;
			const nextColumn = ((_resizeColumn === nRight) ? nMax : _resizeColumn) + 1;
			const rows = headNode.childNodes;

			for (let row of rows) {
				let cell = memoryTable.getCellAtIndex(row, _resizeColumn, _resizeColumn + 1);
				if (cell) {
					cell = memoryTable.getCellAtIndex(row, _resizeColumn, nextColumn);
					cell.classList.toggle(CSS_COLUMN_RESIZE_HEADER, add);
				}
			}

			tableScrollNode.classList.toggle(CSS_COLUMN_RESIZE_CONTAINER, add);

			// class needed on body to affect the cursor everywhere
			document.body.classList.toggle(CSS_COLUMN_RESIZE, add);
		},

		/**
		 * Update table and column widths based on available width
		 */
		_resizeWidth: function() {
			const {
				bodyTableScrollNode,
				headerWidths,
				placeholderNode,
				view
			} = this;

			const {headTableNode, tableContainerNode, tableScrollNode} = view;
			const bFrozen = this.isFrozen();
			const nFrozen = this._calculateFreezeColumn();

			if (nFrozen >= 0) {
				// freeze table
				const oldFrozen = this.nFrozen;
				const bFrozenSame = bFrozen && (nFrozen === oldFrozen);
				let pos = 0;

				this.nFrozen = nFrozen;
				this.nMax = view.numberOfDataColumns - 1;
				this._calculateFrozenWidth();

				if (bFrozenSame) {
					// maintain current scroll position as best as possible
					pos = Math.min(this.scrollPosition, this.maxScrollPosition);
				} else if (!bFrozen) {
					this.nLeft = nFrozen + 1;
					this.nRight = this.nMax;

					// no need for extra column when table is frozen
					tableContainerNode.classList.remove(CSS_NATURAL_HORIZONTAL_SCROLL);
					tableContainerNode.classList.remove(CSS_HORIZONTAL_SCROLL);
					tableScrollNode.classList.remove(CSS_EXTRA_SHOW);
					tableScrollNode.classList.add(CSS_EXTRA_HIDE, CSS_FROZEN);
				}

				// this can affect headerWidths, so must be done before fixing column widths
				this.scrollPosition = -1;
				this._scroll(pos, false, true, oldFrozen);
			} else {
				// determine if there is extra space to fill; if in popup, then use minimum width rather
				// than container width which is set to maximum allowed
				const {bodyWidth, containerWidth, minPopupWidth} = this;
				let actualWidth = minPopupWidth || containerWidth;
				const diff = actualWidth - bodyWidth;
				const actualHeaderWidths = _.clone(headerWidths);
				const showExtra = !view.hideExtraColumn && (diff > 0);
				const bHorizontalScroll = diff <= 0;

				if (showExtra) {
					// allocate remaining space to extra column
					actualHeaderWidths[view.numberOfColumns - 1] = diff;
				} else {
					// remove extra column if not being used
					if (!view.hideExtraColumn) {
						actualHeaderWidths.pop();
					}

					// for popup views that are over the minimum width, the target width is the body width
					if (view.bPopup) {
						actualWidth = bodyWidth;
					}
				}

				const bNaturalScroll = this.useNaturalHorizontalScrolling();
				const headTableScrollNode = headTableNode.parentElement;

				tableContainerNode.classList.toggle(CSS_NATURAL_HORIZONTAL_SCROLL, bNaturalScroll);
				tableContainerNode.classList.toggle(CSS_HORIZONTAL_SCROLL, bNaturalScroll && bHorizontalScroll);
				tableScrollNode.classList.toggle(CSS_EXTRA_HIDE, !showExtra);
				tableScrollNode.classList.toggle(CSS_EXTRA_SHOW, showExtra);

				// unfreeze table (if frozen) and update column widths
				const hScrollPos = headTableScrollNode.scrollLeft
				this._unfreeze();
				this._setColumnWidths(actualWidth, actualHeaderWidths);
				this.refreshHeader();
				headTableScrollNode.scrollLeft = hScrollPos;

				// Prevent header table from scrolling horizontally if body table is not scrolling
				headTableScrollNode.classList.toggle("csi-overflow-hidden", !(bNaturalScroll && html.isScrollingHorizontally(bodyTableScrollNode)));
			}

			// ensure that placeholder shows footer to allow horizontal scrolling if frozen, and hides otherwise
			if (placeholderNode && (this.isFrozen() != bFrozen)) {
				this.updatePlaceholder();
			}
		},

		/**
		 * Move existing left/right scroll borders to new left and right borders
		 * @param {number} newPosition - new scroll position
		 * @param {boolean} [fromSlider] - set to true when call is coming from a change to the frozen slider position
		 * @param {boolean} [fromResize] - set to true when table is resizing, as opposed to just scrolling
		 * @param {number} [oldFrozen] - the last column to be frozen if table is resizing with possibly new frozen column
		 */
		_scroll: function(newPosition, fromSlider, fromResize, oldFrozen) {
			if ((newPosition < 0) || (newPosition > this.maxScrollPosition)) {
				return;
			}

			if ((newPosition === this.scrollPosition) && !fromResize) {
				return;
			}

			const {headerWidths, nFrozen, nLeft, nRight, view} = this;

			if (!view.isValidView()) {
				return;
			}

			const scrollNode = view.tableScrollNode;
			const maxWidth = newPosition + this.frozenScrollWidth;
			const columnWidths = [];
			let column = nFrozen + 1;
			let columnWidth;
			let diff;
			let newLeft;
			let newRight;
			let width = 0;

			// add column widths for frozen columns
			for (let i = 0; i <= nFrozen; ++i) {
				columnWidths.push(headerWidths[i]);
			}

			// find column at left scroll position
			while (width <= newPosition) {
				columnWidth = headerWidths[column++];
				width += columnWidth;
			}

			// store left column and update its width
			newLeft = this.nLeft = column - 1;
			diff = width - newPosition;
			columnWidths.push(diff);

			// apply CSS variables for showing partial column content
			this.frozenLeftOffset = columnWidth - diff;
			html.setCSSVariable(scrollNode, "TableFrozenLeftOffset", columnWidth - diff, true);
			html.setCSSVariable(scrollNode, "TableFrozenLeftWidth", columnWidth, true);

			// find column at right scroll position
			while (width < maxWidth) {
				columnWidth = headerWidths[column++];
				columnWidths.push(columnWidth);
				width += columnWidth;
			}

			// store right column and update its width
			newRight = this.nRight = column - 1;
			diff = this.frozenRightOffset = width - maxWidth;
			columnWidths[columnWidths.length - 1] -= diff;

			// apply CSS variables for showing partial column content
			html.setCSSVariable(scrollNode, "TableFrozenRightWidth", columnWidth, true);

			// if just scrolling, or table was not previously frozen, then no "scrolling" is necessary for frozen columns
			if (!fromResize || (oldFrozen < 0)) {
				oldFrozen = nFrozen;
			}

			// set new properties
			this.scrollPosition = newPosition;

			scrollNode.classList.toggle(CSS_FROZEN_MIN, newPosition === 0);
			scrollNode.classList.toggle(CSS_FROZEN_MAX, newPosition === this.maxScrollPosition);

			// apply columns widths to DOM
			if (this._hasRowActions()) {
				columnWidths.push(headerWidths[this.nMax + 1]);
			}

			const columnWidthTotal = columnWidths.reduce((memo, w) => memo + w, 0);
			console.assert(columnWidthTotal === this.containerWidth, "Table scroll columns mismatch");

			this._setColumnWidths(this.containerWidth, columnWidths, !fromResize);

			// create maps of old and new columns
			function mark(attr, min, max) {
				for (let i = min; i <= max; ++i) {
					attr[i] = 1;
				}
			}

			const oldColumnMap = {};
			const newColumnMap = {};

			mark(oldColumnMap, 0, oldFrozen);
			mark(oldColumnMap, nLeft, nRight);
			mark(newColumnMap, 0, nFrozen);
			mark(newColumnMap, newLeft, newRight);

			// apply changes to rows if any changes to columns in view
			const memoryTable = view.memoryTable;
			const processRow = row => {
				memoryTable.scrollRow(row, oldFrozen, nFrozen, nLeft, newLeft, nRight, newRight, oldColumnMap, newColumnMap);
			};

			_.each(view.headNode.childNodes, processRow);
			_.each(view.bodyNode.childNodes, processRow);

			// update scrollbar
			if (!fromSlider) {
				this._setFrozenScrollbar();
			}

			// ensure sizing of header is correct
			this.refreshHeader();
		},

		/**
		 * Create colgroup nodes for tables
		 * @param {number} tableWidth
		 * @param {number[]} columnWidths - this function will modify this array
		 * @param {boolean} [bOverwrite]
		 */
		_setColumnWidths: function(tableWidth, columnWidths, bOverwrite) {
			const {view} = this;
			const {hideScrollbar, tableContainerNode, tableScrollNode} = view;

			csiTable.fixTableWidths(view.getBodyTableNode(), tableWidth, columnWidths, bOverwrite);

			// rare case, but for some small tables scrolling is disabled
			tableScrollNode.classList.toggle("csi-table-hide-scrollbar", !!hideScrollbar);

			// account for extra column in header to live atop scrollbar in body table
			if (!hideScrollbar) {
				const scrollbarWidth = csiTable.getTableScrollbarWidth();
				html.setCSSVariable(tableContainerNode, "TableScrollbarWidth", scrollbarWidth, true);
				const scrollbarHeight = html.getHorizontalScrollbarHeight()
				html.setCSSVariable(tableContainerNode, "TableScrollbarHeight", scrollbarHeight, true);
				columnWidths.push(scrollbarWidth);
				tableWidth += scrollbarWidth;
			}

			this.columnWidths = columnWidths;
			csiTable.fixTableWidths(view.getHeadTableNode(), tableWidth, columnWidths, bOverwrite);
		},

		/**
		 * Show the frozen indicator based on the flag
		 * @param {boolean} active if true, show the indicator, otherwise, hide it
		 */
		_setFrozenIndicator: function(active) {
			const {
				headTableNode,
			} = this.view;

			headTableNode.classList.toggle(CSS_FROZEN_TABLE_HEADER_ACTIVE, active);
		},

		/**
		 * Create the horizontal scrollbar for frozen tables if it does not exist; otherwise, just update its value;
		 * the value of the scrollbar will be that of the column to display on the right of the frozen area (this.nRight)
		 */
		_setFrozenScrollbar() {
			const {frozenScrollWidth, maxScrollPosition, scrollPosition, view} = this;
			let {frozenSlider} = this;

			if (!frozenSlider) {
				const div = html.create("div", {
					"class": CSS_FROZEN_SLIDER
				}, view.footContentNode, "before");

				this.frozenSlider = frozenSlider = new _TableSlider({
					onChange: (value) => {
						this._throttleScroll(value, true);
					},
				}).placeAt(div);
			}

			frozenSlider.set({
				arrowIncrement: _getStepIncrement(frozenScrollWidth, maxScrollPosition),
				discreteValues: maxScrollPosition + 1,
				maximum: maxScrollPosition,
				pageIncrement: _getPageIncrement(frozenScrollWidth, maxScrollPosition),
			});

			frozenSlider.set("value", scrollPosition, false);
		},

		/**
		 * Set the column border of the table head to either dark color or light color
		 * @param {Event} e
		 * @param {string} key - the key to the styling variables, COLUMN_INDICATOR_KEY_POTENTIAL, COLUMN_INDICATOR_KEY_FREEZEING, COLUMN_INDICATOR_KEY_FROZEN
		 * @param {number} [columnIndex] - if specified, then apply styling to this column instead
		 */
		_setTableHeadFrozenColumnStyling: function(e, key, columnIndex) {
			const {view} = this;
			const {headTableNode, memoryTable} = view;
			const index = e ? view.getHeaderElementMaxIndex(e.target) : columnIndex;
			const minIndex = view.allowsSelections() ? 1 : 0;

			if (index < minIndex) {
				return;
			} else if ((index - minIndex) >= constants.TABLE_MAX_FREEZE_COLUMNS) {
				return;
			}

			[...headTableNode.rows].forEach((row) => {
				const cell = memoryTable.getCellAtIndex(row, index);
				if (cell) {
					// in the case of matrix, cell could be null
					cell.classList.toggle(CSS_POTENTIAl_COLUMN_INDICATOR, true);

					const color = FROZEN_COLUMN_INDICATOR_MAP[key];
					const width = (key == COLUMN_INDICATOR_KEY_FROZEN) ? 1 : 2;

					html.setCSSVariable(cell, "TablePotentialColumnBorderColor", `var(--${color})`);
					html.setCSSVariable(cell, "TablePotentialColumnBorderWidth", width, true);
				}
			});
		},

		/**
		 * Throttles horizontal scrolling - set for 50ms for relatively smooth scrolling
		 * @param {number} pos
		 * @param {boolean} fromSlider
		 */
		_throttleScroll: _.throttle(function(pos, fromSlider) {
			this._scroll(pos, fromSlider);
		}, SCROLL_TIMEOUT),

		/**
		 * Reset any DOM changes done for frozen table
		 */
		_unfreeze() {
			const {view, nFrozen, nLeft, nRight} = this;
			const {memoryTable, tableScrollNode: {classList}} = view;

			if (!classList.contains(CSS_FROZEN)) {
				return;
			}

			const processRow = (row) => {
				memoryTable.unfreezeRow(row, nFrozen, nLeft, nRight);
			};

			classList.remove(CSS_FROZEN, CSS_FROZEN_MAX, CSS_FROZEN_MIN);

			_.each(view.headNode.childNodes, processRow);
			_.each(view.bodyNode.childNodes, processRow);

			// reset frozen properties
			this.nFrozen = this.nMax = this.nLeft = this.nRight = -1;
			this.frozenLeftOffset = this.frozenRightOffset = 0;
		},

		/**
		 * Return true if table should not be frozen and natural scrolling is enabled
		 * @return {boolean}
		 */
		useNaturalHorizontalScrolling: function() {
			return !this.view?.get("tableFreezeColumns") && api.allowNonFrozenTables();
		},

	});
});